import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  show_logo,
  show_logo_text,
  logo,
  logo_text,
  ManualsExportType
} from '@/views/companies/settings/manuals-export/config.ts';
import { EMPTY_SETTING_VALUE, isSettingEmpty } from 'views/companies/settings/helpers';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Switch from '@/library/components/form/inputs/Switch';
import Input from '@/library/components/form/inputs/Input';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatDisplayValue } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type ManualsExportProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.manuals_export;
const CATEGORY_TITLE = 'Manuals Export';

const LABELS = {
  [logo]: 'Logo URL',
  [logo_text]: 'Logo text'
};

const ManualsExport: FC<ManualsExportProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { changeField, selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<ManualsExportType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    const logoValue = companySettings[logo];
    const logoTextValue = companySettings[logo_text];

    loadValues({
      [show_logo]: !isSettingEmpty(logoValue),
      [show_logo_text]: !isSettingEmpty(logoTextValue),
      [logo]: isSettingEmpty(logoValue) ? null : logoValue,
      [logo_text]: isSettingEmpty(logoTextValue) ? null : logoTextValue
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as ManualsExportType | null,
    toPayload: values => [
      {
        key: logo,
        value: values[show_logo] && values[logo] ? values[logo] : EMPTY_SETTING_VALUE
      },
      {
        key: logo_text,
        value: values[show_logo_text] && values[logo_text] ? values[logo_text] : EMPTY_SETTING_VALUE
      }
    ],
    labels: LABELS,
    formatValue: (_key, value) => formatDisplayValue(value),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        className="cpb-2 cmb-2"
        title="Header - Left Part"
        hidden={!isSettingVisible(CATEGORY_ID, logo) && !isSettingVisible(CATEGORY_ID, logo_text)}
      >
        <VisibleSetting categoryId={CATEGORY_ID} settingKey={logo}>
          <Row className="d-flex align-items-center text-violet gx-1 mt-2 fs-12">
            <Col xs={3}>
              <Row className="d-flex align-items-top">
                <Col xs={6} className="d-flex align-items-center h-24">
                  <Switch
                    className="mb-0"
                    css={css`
                      .slide-label {
                        text-transform: none;
                      }
                    `}
                    size="sm"
                    label="Show logo"
                    switchClassName="turquoise-switch"
                    labelClassName="cme-12 fs-12 fw-medium user-select-none"
                    value={fields[show_logo].value}
                    onChange={() => selectField(show_logo)(!fields[show_logo].value)}
                  />
                </Col>
                {fields[show_logo].value ? (
                  <Col xs={6}>
                    <Input
                      className="mb-0"
                      innerClassName="h-24"
                      label={<InputLabel title="LOGO URL" apiKey={'logo'} />}
                      placeholder="Add URL"
                      onChange={changeField(logo)}
                      {...fields[logo]}
                    />
                  </Col>
                ) : null}
              </Row>
            </Col>
          </Row>
        </VisibleSetting>

        <VisibleSetting categoryId={CATEGORY_ID} settingKey={logo_text}>
          <Row className="d-flex align-items-center text-violet gx-1 mt-2 fs-12">
            <Col xs={3}>
              <Row className="d-flex align-items-top">
                <Col xs={6} className="d-flex h-24 cpt-4">
                  <Switch
                    className="mb-0"
                    css={css`
                      .slide-label {
                        text-transform: none;
                      }
                    `}
                    size="sm"
                    label="Show text"
                    switchClassName="turquoise-switch"
                    labelClassName="cme-12 fs-12 fw-medium user-select-none"
                    value={fields[show_logo_text].value}
                    onChange={() => selectField(show_logo_text)(!fields[show_logo_text].value)}
                  />
                </Col>
                {fields[show_logo_text].value ? (
                  <Col xs={4}>
                    <Input
                      className="mb-0"
                      innerClassName="h-24"
                      label={<InputLabel title="LOGO TEXT" apiKey={'logo_text'} />}
                      placeholder="Add text"
                      onChange={changeField(logo_text)}
                      {...fields[logo_text]}
                    />
                  </Col>
                ) : null}
              </Row>
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>
    </Category>
  );
};

export default ManualsExport;
